import { useStore } from '../store';
import type { RichVessel } from '../data/vesselEnrich';
import { VESSEL_ICON_LABELS } from '../data/vesselEnrich';
import { fmtSeen } from '../lib/format';
import { cls } from '../lib/format';

interface Props {
  vessel: RichVessel | null;
}

export function VesselPanel({ vessel }: Props) {
  const open = useStore((s) => s.panelOpen);
  const setOpen = useStore((s) => s.setPanelOpen);
  const setSelected = useStore((s) => s.setSelectedVessel);

  if (!vessel) return null;

  const speedKn = vessel.speed != null ? `${Math.round(vessel.speed)} kn` : '—';
  const heading = vessel.heading ?? vessel.course;
  const kindLabel = VESSEL_ICON_LABELS[vessel.iconKind] ?? vessel.iconKind;
  const title = vessel.name ?? vessel.mmsi ?? vessel.id;

  return (
    <>
      {open ? (
        <div className="fixed inset-0 z-40 bg-black/30 sm:hidden" onClick={() => setOpen(false)} />
      ) : null}
      <section
        className={cls(
          'fixed right-0 z-50 flex w-full flex-col overflow-hidden border-l border-bg-border bg-bg-panel/95 backdrop-blur transition-transform duration-200',
          'bottom-0 h-[68vh] max-h-[68vh] rounded-t-2xl sm:top-0 sm:h-full sm:max-h-full sm:max-w-md sm:rounded-none',
          open ? 'translate-y-0 sm:translate-x-0' : 'translate-y-full sm:translate-y-0 sm:translate-x-full',
        )}
        style={{ paddingBottom: 'var(--safe-bottom)' }}
        aria-label="Vessel details"
      >
        <div className="flex shrink-0 items-center justify-between px-4 pt-3">
          <div className="mx-auto h-1.5 w-10 rounded-full bg-bg-border sm:hidden" />
          <button
            onClick={() => {
              setSelected(null);
              setOpen(false);
            }}
            className="ml-auto rounded-md bg-bg-raised px-2.5 py-1 text-xs text-slate-300"
          >
            Close
          </button>
        </div>

        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          <div className="mt-1">
            <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
            <p className="text-sm text-slate-400">{kindLabel}</p>
            {vessel.callsign ? <p className="font-mono text-xs text-slate-500">{vessel.callsign}</p> : null}
          </div>

          <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
            {vessel.vesselType ? (
              <>
                <dt className="text-slate-500">Type</dt>
                <dd className="text-slate-200">{vessel.vesselType}</dd>
              </>
            ) : null}
            {vessel.flag ? (
              <>
                <dt className="text-slate-500">Flag</dt>
                <dd className="text-slate-200">{vessel.flag}</dd>
              </>
            ) : null}
            <dt className="text-slate-500">Speed</dt>
            <dd className="text-slate-200">{speedKn}</dd>
            {heading != null ? (
              <>
                <dt className="text-slate-500">Heading</dt>
                <dd className="text-slate-200">{Math.round(heading)}°</dd>
              </>
            ) : null}
            {vessel.destination ? (
              <>
                <dt className="text-slate-500">Destination</dt>
                <dd className="text-slate-200">{vessel.destination}</dd>
              </>
            ) : null}
            {vessel.navStatus ? (
              <>
                <dt className="text-slate-500">Status</dt>
                <dd className="text-slate-200">{vessel.navStatus}</dd>
              </>
            ) : null}
            <dt className="text-slate-500">Seen</dt>
            <dd className="text-slate-200">{fmtSeen(vessel.seen)}</dd>
            {vessel.mmsi ? (
              <>
                <dt className="text-slate-500">MMSI</dt>
                <dd className="font-mono text-slate-200">{vessel.mmsi}</dd>
              </>
            ) : null}
          </dl>
        </div>
      </section>
    </>
  );
}
